/**
 * ConfirmModal - Confirmation dialog for web.
 * Shows title, message, and Cancel/Confirm buttons.
 * Supports destructive styling for delete/remove actions.
 * Uses Framer Motion for smooth fade/scale animation.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Info, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  isDestructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  isDestructive = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps): ReactElement | null {
  // Portaled so a transformed ancestor (the picks card scales on hover) cannot become the
  // containing block for the fixed overlay.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/50"
            onClick={onCancel}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6"
          >
            {/* Close button */}
            <button
              type="button"
              onClick={onCancel}
              data-testid="confirm-modal-close"
              className="absolute top-3 right-3 p-1 rounded-md text-theme-text-tertiary hover:text-theme-text-secondary hover:bg-theme-surface-hover transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  isDestructive ? 'bg-red-600/15' : 'bg-theme-primary/15'
                }`}
              >
                {isDestructive ? (
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                ) : (
                  <Info className="w-8 h-8 text-theme-primary" />
                )}
              </div>
            </div>

            {/* Title */}
            <h3
              className={`text-xl font-bold text-center mb-2 ${
                isDestructive ? 'text-red-500' : 'text-theme-text'
              }`}
            >
              {title}
            </h3>

            {/* Message */}
            <p className="text-theme-text-secondary text-center text-sm mb-6">{message}</p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                data-testid="confirm-modal-cancel"
                className="flex-1 py-2.5 rounded-lg text-theme-text-secondary hover:bg-theme-surface-hover font-medium transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                data-testid="confirm-modal-confirm"
                className={`flex-1 py-2.5 rounded-lg font-medium transition ${
                  isDestructive
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-theme-primary-fill hover:bg-theme-primary-fill/90 text-theme-on-primary-fill'
                }`}
              >
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
